/**
 * Genre API service for admin interface
 * Handles CRUD operations for genres
 */

import type {
  CreateGenreRequest,
  Genre,
  UpdateGenreRequest,
} from '@musedrops/shared';
import {
  createGenre as createGenreRecord,
  deleteGenre as deleteGenreRecord,
  getAllGenres,
  getGenre as fetchGenre,
  getGenreInstructions as fetchGenreInstructions,
  listGenres,
  updateGenre as updateGenreRecord,
} from '../data/genre';
import { getShowsByGenre as listShowsByGenre } from '../data/show';

export class GenreApi {
  static async getAll(): Promise<Genre[]> {
    const { data } = await listGenres();
    return data;
  }

  static async getById(id: string): Promise<Genre> {
    return fetchGenre(id);
  }

  static async create(genreData: CreateGenreRequest): Promise<Genre> {
    return createGenreRecord(genreData);
  }

  static async update(
    id: string,
    genreData: UpdateGenreRequest
  ): Promise<Genre> {
    return updateGenreRecord(id, genreData);
  }

  static async delete(id: string): Promise<void> {
    const associatedShows = await listShowsByGenre(id);

    if (associatedShows.length > 0) {
      throw new Error(
        'Cannot delete genre: There are shows associated with this genre'
      );
    }

    await deleteGenreRecord(id);
  }

  static async getAssociatedShows(
    genreId: string
  ): Promise<{ id: string; name: string; slug: string }[]> {
    const shows = await listShowsByGenre(genreId);
    return shows.map(({ id, name, slug }) => ({ id, name, slug }));
  }

  static async getInstructions(genreId: string): Promise<string> {
    return fetchGenreInstructions(genreId);
  }
}

// Backwards compatibility for callers expecting only enabled genres.
export const getEnabledGenres = getAllGenres;
